<template>
  <PreviewElementWrapper
    :initial-selected="isSelected"
    :element-type="element.type"
    :element="element"
    :selectedPreviewId="selectedPreviewId"
    :disabled="disabled"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @select-change="handleSelectChange"
    @update-element-data="handleWrapperDataUpdate"
    @auto-mapping = "(id)=> $emit('auto-mapping', id)"
    @element-edit-state-changed="({isEditing})=>handleWrapperEditStateChanged(isEditing)"
  >
    <div class="options-container">
      <div :class="['option-item', { 'is-editing': isEditing }]">
        <div class="checkbox">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="0.5" y="0.5" width="19" height="19" rx="3.5" fill="white" stroke="#004879"/>
          </svg>
        </div>
        <div v-if="isEditing" class="option-edit-box">
          <input
            ref="optionInput"
            v-model="option"
            placeholder="Option"
            class="option-edit-input"
            @keyup.enter="handleSaveOption"
            @keyup.esc="handleCancelOptionEdit"
          />
          <TextButton
            @click.stop="handleSaveOption"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M6.75012 12.1275L3.62262 9L2.55762 10.0575L6.75012 14.25L15.7501 5.25L14.6926 4.1925L6.75012 12.1275Z" fill="#1F1F1F"/>
              </svg>
            </template>
            Save
          </TextButton>
        </div>
        <div v-else class="option-text">{{ option }}</div>

        <div v-if = "!disabled && (isHovered || isSelected)" class="action-buttons">
          <!-- Original: 
          <ActionButton
            v-if="!isEditing"
            class="edit-btn"
            text="Edit"
            default-color="#374957"
            hover-text-color="#1E4670"
            pressed-text-color="#1E4670"
            hover-bg-color="rgba(30, 70, 112, 0.08)"
            pressed-bg-color="rgba(30, 70, 112, 0.12)"
            @click.stop="handleStartOptionEdit"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M18.8969 3.16575C19.0892 3.45687 19.0571 3.85248 18.8008 4.10876L9.60845 13.3011C9.51422 13.3954 9.39659 13.4628 9.26766 13.4965L5.43923 14.4965C5.31437 14.5291 5.18566 14.5284 5.06473 14.4976C4.93616 14.4649 4.81639 14.3982 4.71937 14.3011C4.5311 14.1129 4.45676 13.8389 4.52405 13.5813L5.52405 9.75284C5.55351 9.64003 5.60881 9.52861 5.68529 9.44121L14.9117 0.21967C14.9925 0.138906 15.0898 0.0784602 15.1957 0.0416313C15.2742 0.0143243 15.3574 0 15.4421 0C15.641 0 15.8318 0.0790175 15.9724 0.21967L18.8008 3.0481C18.8374 3.08471 18.8695 3.12416 18.8969 3.16575ZM17.2098 3.57843L15.4421 1.81066L6.92391 10.3288L6.29893 12.7216L8.69169 12.0966L17.2098 3.57843Z" fill="#374957"/>
                <path d="M17.0834 14.9103C17.3568 12.5727 17.4438 10.2188 17.3444 7.87079C17.3397 7.75837 17.3819 7.64898 17.4615 7.56942L18.4448 6.58609C18.5657 6.46519 18.7723 6.54194 18.7836 6.71254C18.9685 9.50219 18.8984 12.3045 18.5733 15.0846C18.3367 17.1071 16.7124 18.6921 14.7003 18.917C11.2338 19.3044 7.65042 19.3044 4.18385 18.917C2.17179 18.6921 0.547457 17.1071 0.310903 15.0846C-0.103634 11.5403 -0.103635 7.95973 0.310903 4.41543C0.547456 2.3929 2.17179 0.807889 4.18385 0.583012C6.8136 0.289099 9.51054 0.218149 12.1727 0.370161C12.3443 0.379958 12.4225 0.587575 12.301 0.709093L11.3084 1.70165C11.2298 1.78034 11.1218 1.82261 11.0106 1.81885C8.78419 1.74376 6.54263 1.82872 4.35046 2.07373C3.02035 2.22239 1.95486 3.272 1.80075 4.58968C1.39975 8.01821 1.39975 11.4818 1.80075 14.9103C1.95486 16.228 3.02035 17.2776 4.35046 17.4263C7.70631 17.8013 11.1779 17.8013 14.5337 17.4263C15.8638 17.2776 16.9293 16.228 17.0834 14.9103Z" fill="#374957"/>
              </svg>
            </template>
          </ActionButton>
          -->
          <FoldableButton
            v-if="!isEditing"
            text="Edit"
            built_in_theme="default"
            @click.stop="handleStartOptionEdit"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M18.8969 3.16575C19.0892 3.45687 19.0571 3.85248 18.8008 4.10876L9.60845 13.3011C9.51422 13.3954 9.39659 13.4628 9.26766 13.4965L5.43923 14.4965C5.31437 14.5291 5.18566 14.5284 5.06473 14.4976C4.93616 14.4649 4.81639 14.3982 4.71937 14.3011C4.5311 14.1129 4.45676 13.8389 4.52405 13.5813L5.52405 9.75284C5.55351 9.64003 5.60881 9.52861 5.68529 9.44121L14.9117 0.21967C14.9925 0.138906 15.0898 0.0784602 15.1957 0.0416313C15.2742 0.0143243 15.3574 0 15.4421 0C15.641 0 15.8318 0.0790175 15.9724 0.21967L18.8008 3.0481C18.8374 3.08471 18.8695 3.12416 18.8969 3.16575ZM17.2098 3.57843L15.4421 1.81066L6.92391 10.3288L6.29893 12.7216L8.69169 12.0966L17.2098 3.57843Z" fill="#374957"/>
                <path d="M17.0834 14.9103C17.3568 12.5727 17.4438 10.2188 17.3444 7.87079C17.3397 7.75837 17.3819 7.64898 17.4615 7.56942L18.4448 6.58609C18.5657 6.46519 18.7723 6.54194 18.7836 6.71254C18.9685 9.50219 18.8984 12.3045 18.5733 15.0846C18.3367 17.1071 16.7124 18.6921 14.7003 18.917C11.2338 19.3044 7.65042 19.3044 4.18385 18.917C2.17179 18.6921 0.547457 17.1071 0.310903 15.0846C-0.103634 11.5403 -0.103635 7.95973 0.310903 4.41543C0.547456 2.3929 2.17179 0.807889 4.18385 0.583012C6.8136 0.289099 9.51054 0.218149 12.1727 0.370161C12.3443 0.379958 12.4225 0.587575 12.301 0.709093L11.3084 1.70165C11.2298 1.78034 11.1218 1.82261 11.0106 1.81885C8.78419 1.74376 6.54263 1.82872 4.35046 2.07373C3.02035 2.22239 1.95486 3.272 1.80075 4.58968C1.39975 8.01821 1.39975 11.4818 1.80075 14.9103C1.95486 16.228 3.02035 17.2776 4.35046 17.4263C7.70631 17.8013 11.1779 17.8013 14.5337 17.4263C15.8638 17.2776 16.9293 16.228 17.0834 14.9103Z" fill="#374957"/>
              </svg>
            </template>
          </FoldableButton>
        </div>
      </div>
    </div>
  </PreviewElementWrapper>
</template>

<script>
import PreviewElementWrapper from './PreviewElementWrapper.vue';
// Original: import ActionButton from '@/views/commonComponents/ActionButton.vue';
// Replaced with globally registered FoldableButton from @vibrant-wellness/va-responsive-components-library
import TextButton from '@/views/commonComponents/TextButton.vue';

export default {
  name: 'CheckboxPreview',
  components: { PreviewElementWrapper, /* ActionButton, replaced by globally registered FoldableButton */ TextButton },
  props: {
    element: { type: Object, default: () => ({}) },
    selectedPreviewId: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return {
      option: this.element.data?.option,
      isEditing: false,
      isHovered: false,
      isSelected: false,
      isWrapperEditing: false
    };
  },
  emits: [
    'element-edit-state-changed',
    'update-element-data',
    'auto-mapping'
  ],
  computed: {
    isElementEditing() {
      return this.isWrapperEditing || this.isEditing;
    }
  },
  watch:{
    isElementEditing(newVal) {
      // pass the edit state to parent
      this.$emit('element-edit-state-changed',{ id: this.element.id, isEditing: newVal });
    }
  },
  methods: {
    handleMouseEnter() {
      if (!this.disabled) {
        this.isHovered = true;
      }
    },
    handleMouseLeave() {
      this.isHovered = false;
    },
    handleWrapperEditStateChanged(isEditing) {
      this.isWrapperEditing = isEditing;
    },
    handleSelectChange(isSelected) {
      this.isSelected = isSelected;
      if (!isSelected) {
        this.handleSaveOption();
      }
    },
    handleSaveOption() {
      this.isEditing = false;
      this.emitDataUpdate();
    },
    handleCancelOptionEdit() {
      this.isEditing = false;
    },
    handleStartOptionEdit() {
      if (this.disabled) {
        return;
      }
      this.isSelected = true;
      this.isEditing = true;
      this.$nextTick(() => {
        const input = this.$refs.optionInput;
        input?.focus();
        input?.select();
      });
    },
    handleWrapperDataUpdate(wrapperData) {
      this.$emit('update-element-data', { ...wrapperData, option: this.option });
    },
    emitDataUpdate() {
      this.$emit('update-element-data', { option: this.option });
    }
  }
};
</script>

<style scoped>
.options-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.5s ease;
}
.option-item:hover,
.option-item.is-editing {
  padding: 2px 0px;
  background-color: #F2F2F2;
}
.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
}

.option-edit-box {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  padding: 3px 4px;
  gap: 4px;
  flex: 1;
  height: 26px;
  min-width: 0;
}

.option-edit-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #333;
  min-width: 0;
}

.option-text {
  flex: 1;
  font-family: 'Inter', sans-serif;
  color: #333;

  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 125% */
  letter-spacing: 0.15px;

  min-width: 0;
}

:deep(.text-button) {
  padding: 1px 6px;
}

</style>
